import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Zap, Leaf, ShieldCheck, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { ABOUT_STATS, IMAGES } from "../data/mock";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

const valueIcons = [Eye, Zap, Leaf, ShieldCheck];

const About = () => {
  const { lang } = useLanguage();
  const t = translations[lang].aboutPage;

  return (
    <>
      <Seo title={t.hero} description={t.heroDesc} path="/hakkimizda" lang={lang} />
      <PageHero title={t.hero} desc={t.heroDesc} />

      {/* Story */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={IMAGES.team1}
                alt="AdalCrea Dijital Dönüşüm Kiti"
                className="rounded-2xl w-full h-[420px] object-cover object-center"
              />
              <div className="absolute -bottom-6 -right-6 hidden sm:block rounded-2xl bg-amber-500 p-6 shadow-xl">
                <div className="font-display font-extrabold text-3xl text-navy-950">7/24</div>
                <div className="text-sm text-navy-900 font-medium">
                  {lang === "tr" ? "Proaktif İzleme" : "Proactive Monitoring"}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">
              <span className="w-6 h-px bg-amber-500" />
              {translations[lang].nav.about}
            </span>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl text-navy-900 leading-tight">
              {t.storyTitle}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{t.story1}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{t.story2}</p>
            <Link
              to="/iletisim"
              className="mt-8 inline-flex items-center gap-2 bg-navy-900 hover:bg-navy-800 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {translations[lang].ctaBanner.button}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-navy-900 text-center">
            {t.valuesTitle}
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v.title} delay={i * 100}>
                  <div className="h-full rounded-2xl bg-white border border-slate-200 p-7 hover:border-amber-500/50 hover:shadow-lg transition-all">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="mt-5 font-display font-bold text-lg text-navy-900">
                      {v.title}
                    </h3>
                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-navy-950 py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-white text-center mb-12">
            {t.statsTitle}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {ABOUT_STATS.map((s, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="text-center">
                  <div className="font-display font-extrabold text-4xl lg:text-5xl text-amber-400">
                    {s.value}
                  </div>
                  <div className="mt-2 text-sm text-slate-300">{s.label[lang]}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
